import { Box, Flex, Text } from '@chakra-ui/react';
import { ErrorMessage, Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';

import {
  addStepTwoFormData,
  handleActionStepNext,
  getFormValues,
  clearStepperFormPropertyData,
  getNoheadersStatus,
  setNoheaders,
} from 'redux/DataImportSlice';
import { ActionSteptwoSchemaDataType } from 'validation/schemas/ActionStepSchema';
import { getBlocks } from 'redux/ModelsSlice';
import { fetchAllScenarios, getAllScenarios } from 'redux/ScenarioSlice';
import { Scenario } from 'models/response/scenario.interface';
import { PLAN_TEXT } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';

import colors from '../../dataTheme';
import ActionStepFooter from '../ActionStepFooter';
import { FieldError, FieldLabel, StepCheckbox, StepFooterRow, StepSelect } from '../actionStepUi';

interface Properties {
  setHelpModalSeleceted: any;
  dataType: string;
}

const DataType = ({ dataType }: Properties) => {
  const dispatch: any = useDispatch();
  const { modelId } = useParams();
  const [selectBlockId, setSelectBlockId] = useState<number | null>();
  const getFormValue = useSelector(getFormValues);
  const allScenarios = useSelector(getAllScenarios);
  const [selectBlockDetails, setSelectBlockDetails] = useState<any>(null);
  const isHeader = useSelector(getNoheadersStatus);
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>();
  const [selectedScenarioDetails, setSelectedScenarioDetails] = useState<any>('');
  const dimesnsionList = useSelector(getBlocks);

  // Ensure scenarios are fetched when setting up data/forecast import (they may not be loaded yet)
  useEffect(() => {
    if (
      (dataType === '2' || dataType === '3') &&
      modelId &&
      (!allScenarios || allScenarios.length === 0)
    ) {
      dispatch(fetchAllScenarios(Number(modelId)));
    }
  }, [dataType, modelId, allScenarios?.length, dispatch]);

  useMemo(() => {
    const calculateSelectedBlockDetails = dimesnsionList?.blocks?.find((item: any) => {
      if (selectBlockId && !getFormValue.blockId && item?.id === selectBlockId) {
        return item;
      }
      if (getFormValue?.blockId && !selectBlockId && item?.id === Number(getFormValue?.blockId)) {
        return item;
      }
      if (getFormValue?.blockId && selectBlockId && item?.id === selectBlockId) {
        return item;
      }
      return null;
    });
    setSelectBlockDetails(calculateSelectedBlockDetails);
  }, [selectBlockId, getFormValue.blockId]);
  useMemo(() => {
    const calculateSelectedScenarioDetails = allScenarios?.find((scenario: Scenario) => {
      if (!selectedScenarioId && !getFormValue.selectedScenario) {
        return scenario.is_base;
      }
      if (
        selectedScenarioId &&
        !getFormValue.selectedScenario &&
        scenario?.id === selectedScenarioId
      ) {
        return scenario;
      }
      if (
        getFormValue?.selectedScenario &&
        !selectedScenarioId &&
        scenario?.id === Number(getFormValue?.selectedScenario)
      ) {
        return scenario;
      }
      if (
        getFormValue?.selectedScenario &&
        selectedScenarioId &&
        scenario?.id === selectedScenarioId
      ) {
        return scenario;
      }
      return null;
    });
    setSelectedScenarioDetails(calculateSelectedScenarioDetails);
  }, [selectedScenarioId, getFormValue.selectedScenario, allScenarios]);

  const handleSubmit = (form: any, setSubmitting: (data: boolean) => void) => {
    dispatch(addStepTwoFormData(form));
    setSubmitting(false);
    dispatch(handleActionStepNext());
  };

  return (
    <Box w='100%' mt={5}>
      <Formik
        initialValues={{
          selectedDataType: '' || dataType || getFormValue.dataType,
          selectedBlock: '' || selectBlockDetails?.id || getFormValue.blockId,
          selectedScenario:
            ('' || selectedScenarioDetails?.id || getFormValue.selectedScenario) ?? '',
        }}
        validationSchema={ActionSteptwoSchemaDataType}
        onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
        enableReinitialize={true}
      >
        {({ values, errors, isSubmitting, setFieldValue }: any) => (
          <Form style={{ width: '100%' }}>
            <ErrorMessage name='selectedDataType' component={FieldError} />
            {(values.selectedDataType === '2' || values.selectedDataType === '3') && (
              <Flex direction='column' gap={4}>
                <Box>
                  <FieldLabel>Select block</FieldLabel>
                  <StepSelect
                    value={values.selectedBlock}
                    placeholder='Select block'
                    isClearable
                    isInvalid={Boolean(errors.selectedBlock)}
                    isDisabled={isSubmitting}
                    options={(dimesnsionList?.blocks ?? []).map((item: any) => ({
                      value: item?.id,
                      label: item?.name,
                    }))}
                    onChange={async (id) => {
                      await setFieldValue('selectedBlock', id);
                      await setSelectBlockId(id === '' ? null : Number(id));
                      await dispatch(clearStepperFormPropertyData());
                    }}
                  />
                  <ErrorMessage name='selectedBlock' component={FieldError} />
                </Box>

                <Box>
                  <FieldLabel>Select scenario</FieldLabel>
                  <StepSelect
                    value={selectedScenarioDetails?.id ?? values.selectedScenario}
                    placeholder='Select scenario'
                    isClearable
                    isInvalid={Boolean(errors.selectedScenario)}
                    isDisabled={isSubmitting}
                    options={(allScenarios ?? []).map((item: any) => ({
                      value: item?.id,
                      label: item?.name,
                    }))}
                    onChange={(id) => {
                      setFieldValue('selectedScenario', id);
                      setSelectedScenarioId(id === '' ? null : Number(id));
                    }}
                  />
                  {(!allScenarios || allScenarios.length === 0) && (
                    <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary} mt='4px'>
                      No scenarios available.
                    </Text>
                  )}
                  <ErrorMessage name='selectedScenario' component={FieldError} />
                </Box>

                <StepCheckbox
                  isChecked={Boolean(isHeader)}
                  onChange={() => dispatch(setNoheaders(!isHeader))}
                >
                  File has no column headers
                </StepCheckbox>
              </Flex>
            )}

            <StepFooterRow>
              <ActionStepFooter
                nextButtonName='Continue'
                prevButtonName='Previous'
                isSubmitting={isSubmitting}
              />
            </StepFooterRow>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default DataType;
